/* eslint-disable @typescript-eslint/no-use-before-define */
// 선제 갱신 스케줄러 ↔ refreshAuthToken ↔ performLogout 는 상호 재귀 관계이므로
// (함수 선언은 호이스팅되어 런타임엔 안전) no-use-before-define 을 파일 단위로 허용한다.
import Cookies from "universal-cookie";
import { keycloak } from "@auth/keycloak";
import { env } from "@configs/Env";
import { queryClient } from "@configs/Queries";
import { getPortalURL } from "@utils/Common";
import {
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  USER_INFO,
  REDIRECT_PARAM
} from "@resources/Constants";
import { logKeycloakToken, tokenDebugInfo } from "./authDebugLog";

/**
 * 토큰 라이프사이클 관리.
 *
 * 핵심 설계:
 * - 토큰은 메모리에만 저장(localStorage 저장 금지) → XSS 토큰 탈취 방지.
 *   새로고침 시 check-sso 가 SSO 쿠키로 세션을 복원한다.
 * - 만료 5분 전 선제(proactive) 갱신.
 * - 같은 탭 내 동시 호출은 하나의 promise 로 dedupe.
 * - 여러 탭이 동시에 갱신하지 않도록 BroadcastChannel + localStorage 락으로 조율.
 */

const TOKEN_MIN_VALIDITY_SECONDS = 30;
const PROACTIVE_REFRESH_BUFFER_SECONDS = 5 * 60; // 만료 5분 전 갱신
const MIN_REFRESH_DELAY_MS = 5_000;
const REQUEST_REFRESH_COOLDOWN_MS = 5_000;
const NEAR_EXPIRY_SECONDS = 45;

const BROADCAST_CHANNEL_NAME = "sightmind_auth_refresh";
const REFRESH_LOCK_KEY = "sightmind_auth_refresh_lock";
const REFRESH_LOCK_TTL_MS = 10_000;

const PLATFORM_REDIRECT_GUARD_KEY = "sightmind_platform_redirect_at";
const PLATFORM_REDIRECT_MIN_INTERVAL_MS = 10_000;

const cookie = new Cookies();

let accessToken: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshPromise: Promise<string | null> | null = null;
let lastRequestRefreshAt = 0;

let channel: BroadcastChannel | null = null;
let tokenManagerInitialized = false;

type BroadcastPayload = {
  type: "COMPLETED" | "FAILED";
  token?: string;
};

// ---------------------------------------------------------------------------
// 메모리 토큰
// ---------------------------------------------------------------------------

export function setAccessToken(token: string | null, source = "unknown"): void {
  accessToken =
    token && token !== "null" && token !== "undefined" ? token : null;
  logKeycloakToken(`setAccessToken(${source})`, tokenDebugInfo(accessToken));
  if (accessToken) scheduleProactiveRefresh();
  else clearScheduledRefresh();
}

export function getAccessToken(): string | null {
  return accessToken;
}

// ---------------------------------------------------------------------------
// JWT 파싱 유틸
// ---------------------------------------------------------------------------

/** JWT payload 의 exp(초)를 파싱. tokenParsed.exp 는 소프트 갱신 후 뒤처질 수 있어 신뢰하지 않는다. */
function decodeJwtExp(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "="
    );
    const decoded = JSON.parse(atob(padded)) as { exp?: number };
    return typeof decoded.exp === "number" ? decoded.exp : null;
  } catch {
    return null;
  }
}

/** 현재 메모리 토큰이 만료 임박(NEAR_EXPIRY_SECONDS 이내)인지. */
export function isAccessTokenNearExpiry(): boolean {
  if (!accessToken) return true;
  const exp = decodeJwtExp(accessToken) ?? keycloak.tokenParsed?.exp;
  if (!exp) return true;
  return exp * 1000 - Date.now() <= NEAR_EXPIRY_SECONDS * 1000;
}

/** 요청 트리거 갱신 쿨다운. 병렬 요청 N개가 각각 갱신 호출하는 것을 방지. */
export function canAttemptRequestRefresh(): boolean {
  const now = Date.now();
  if (now - lastRequestRefreshAt < REQUEST_REFRESH_COOLDOWN_MS) return false;
  lastRequestRefreshAt = now;
  return true;
}

// ---------------------------------------------------------------------------
// 선제 갱신 스케줄러
// ---------------------------------------------------------------------------

function clearScheduledRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleProactiveRefresh(): void {
  clearScheduledRefresh();
  if (!accessToken) return;

  const exp = decodeJwtExp(accessToken) ?? keycloak.tokenParsed?.exp;
  if (!exp) return;

  const now = Date.now();
  const remaining = exp * 1000 - now;
  if (remaining <= 0) return; // 이미 만료 → onTokenExpired 가 처리

  const targetDelayMs =
    exp * 1000 - PROACTIVE_REFRESH_BUFFER_SECONDS * 1000 - now;
  // TTL 이 버퍼보다 짧으면 스핀 루프 방지를 위해 남은 수명의 80% 지점에 예약.
  const delayMs =
    targetDelayMs > MIN_REFRESH_DELAY_MS
      ? targetDelayMs
      : Math.max(MIN_REFRESH_DELAY_MS, Math.floor(remaining * 0.8));

  refreshTimer = setTimeout(() => {
    refreshAuthToken(-1).catch(() => {
      const refreshExp = keycloak.refreshTokenParsed?.exp;
      if (refreshExp && Date.now() >= refreshExp * 1000) performLogout();
    });
  }, delayMs);
}

// ---------------------------------------------------------------------------
// 크로스탭 조율 (BroadcastChannel + localStorage 락)
// ---------------------------------------------------------------------------

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  channel ??= new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  return channel;
}

function broadcast(type: BroadcastPayload["type"], token?: string): void {
  getChannel()?.postMessage({ type, token } satisfies BroadcastPayload);
}

function acquireRefreshLock(): boolean {
  try {
    const now = Date.now();
    const raw = localStorage.getItem(REFRESH_LOCK_KEY);
    if (raw) {
      const lockedAt = Number(raw);
      if (Number.isFinite(lockedAt) && now - lockedAt < REFRESH_LOCK_TTL_MS) {
        return false; // 다른 탭이 갱신 중
      }
    }
    localStorage.setItem(REFRESH_LOCK_KEY, String(now));
    return true;
  } catch {
    return true; // localStorage 불가 환경이면 락 없이 진행
  }
}

function releaseRefreshLock(): void {
  try {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  } catch {
    /* noop */
  }
}

function waitForOtherTabRefresh(): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const ch = getChannel();
    if (!ch) {
      reject(new Error("BroadcastChannel unavailable"));
      return;
    }

    let timeout: ReturnType<typeof setTimeout> | undefined;

    const listener = (event: MessageEvent<BroadcastPayload>) => {
      const { type, token } = event.data ?? {};
      if (type !== "COMPLETED" && type !== "FAILED") return;
      if (timeout) clearTimeout(timeout);
      ch.removeEventListener("message", listener);
      if (type === "COMPLETED") {
        if (token) setAccessToken(token);
        resolve(token ?? getAccessToken());
      } else {
        reject(new Error("Other tab refresh failed"));
      }
    };

    ch.addEventListener("message", listener);
    timeout = setTimeout(() => {
      ch.removeEventListener("message", listener);
      reject(new Error("Timed out waiting for other tab refresh"));
    }, REFRESH_LOCK_TTL_MS);
  });
}

/**
 * BroadcastChannel 을 구독해 다른 탭이 갱신한 토큰(COMPLETED)을 받아 메모리에 반영.
 * idempotent — 여러 번 호출해도 한 번만 구독한다.
 */
export function initializeTokenManager(): void {
  if (tokenManagerInitialized) return;
  tokenManagerInitialized = true;

  const ch = getChannel();
  ch?.addEventListener("message", (event: MessageEvent<BroadcastPayload>) => {
    const { type, token } = event.data ?? {};
    if (type === "COMPLETED" && token) {
      setAccessToken(token);
    }
  });
}

// ---------------------------------------------------------------------------
// 갱신 함수 (동시호출 dedupe + 크로스탭 조율)
// ---------------------------------------------------------------------------

export async function refreshAuthToken(
  minValidity = TOKEN_MIN_VALIDITY_SECONDS
): Promise<string | null> {
  if (!keycloak.authenticated) return accessToken;

  // refresh 토큰 만료 먼저 확인.
  const refreshExp = keycloak.refreshTokenParsed?.exp;
  if (refreshExp && Date.now() >= refreshExp * 1000) {
    performLogout();
    throw new Error("Refresh token expired");
  }

  // 같은 탭 내 동시 호출은 하나의 promise 공유.
  if (refreshPromise) return refreshPromise;

  // 크로스탭: 다른 탭이 갱신 중이면 그 브로드캐스트를 기다린다.
  if (!acquireRefreshLock()) {
    return waitForOtherTabRefresh().catch(() => getAccessToken());
  }

  refreshPromise = keycloak
    .updateToken(minValidity)
    .then(() => {
      const newToken = keycloak.token ?? accessToken;
      broadcast("COMPLETED", newToken ?? undefined);
      setAccessToken(newToken ?? null);
      return newToken ?? null;
    })
    .catch((error: unknown) => {
      broadcast("FAILED");
      throw error instanceof Error ? error : new Error(String(error));
    })
    .finally(() => {
      releaseRefreshLock();
      refreshPromise = null;
      scheduleProactiveRefresh();
    });

  return refreshPromise;
}

// ---------------------------------------------------------------------------
// 로그아웃
// ---------------------------------------------------------------------------

/** 쿠키(access/refresh/userInfo) + react-query 캐시 정리. */
function clearAppSession(): void {
  cookie.remove(ACCESS_TOKEN, { path: "/" });
  cookie.remove(REFRESH_TOKEN, { path: "/" });
  cookie.remove(USER_INFO, { path: "/" });
  queryClient.clear();
}

export type RedirectToPlatformOptions = {
  /** true 이면 10초 루프 가드를 건너뛴다 (401 등 명시적 재로그인). */
  bypassGuard?: boolean;
};

/**
 * 재로그인 진입점(Platform)으로 이동한다.
 * 기존 로그인 흐름과 동일하게 redirect_app_url 에 현재 origin 을 실어
 * Platform → keycloak 인증 후 다시 앱으로 복귀시킨다.
 *
 * 루프 방지: check-sso 가 유효 세션을 계속 못 읽는 경우(3rd-party 쿠키 차단 등)
 * app → platform → app → platform 무한 왕복이 발생할 수 있어,
 * 최근 리다이렉트 직후(같은 origin sessionStorage)면 재이동을 건너뛴다.
 */
export function redirectToPlatformLogin(
  options: RedirectToPlatformOptions = {}
): void {
  const targetUrl = `https://${getPortalURL()}?${REDIRECT_PARAM}=${
    window.location.origin
  }&client_id=${env.keycloakClientId}`;

  if (!options.bypassGuard) {
    try {
      const last = Number(
        sessionStorage.getItem(PLATFORM_REDIRECT_GUARD_KEY) ?? "0"
      );
      if (
        Number.isFinite(last) &&
        Date.now() - last < PLATFORM_REDIRECT_MIN_INTERVAL_MS
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          "[keycloak] redirectToPlatformLogin skipped (loop guard)",
          { targetUrl, msSinceLast: Date.now() - last }
        );
        return;
      }
      sessionStorage.setItem(PLATFORM_REDIRECT_GUARD_KEY, String(Date.now()));
    } catch {
      /* sessionStorage 불가 환경이면 가드 없이 진행 */
    }
  }

  window.location.replace(targetUrl);
}

/**
 * 세션/토큰 정리. Platform 이동은 init 에러(onAuthError)에서만 수행한다.
 */
export function performLogout(): void {
  setAccessToken(null);
  clearAppSession();
  redirectToPlatformLogin({ bypassGuard: true });
}
